/**
 * Centralized TanStack Query key factory.
 *
 * These keys are used by hooks to watch PowerSync tables
 * and auto-invalidate via TanStack Query.
 *
 * Rules:
 *   - Every PowerSync-backed or server-backed query MUST use a key from here.
 *   - Never use inline string arrays as queryKey — use this factory instead.
 *   - Keys are grouped by domain and ordered from broad → specific.
 */
export const queryKeys = {
  habits: {
    all: ["habits"] as const,
    /** User habits (Today / management). */
    list: (userId: string | undefined, includeArchived?: boolean) =>
      ["habits", "list", userId, includeArchived ?? false] as const,
    /** Specific habit (detail/edit). */
    detail: (habitId: string | undefined) => ["habits", "detail", habitId] as const,
    /** Complete user completions history (streaks in Today/stats). */
    completions: (userId: string | undefined) => ["habits", "completions", userId] as const,
    /** Completions de un rango (calendarios). */
    completionsRange: (userId: string | undefined, fromKey: string, toKey: string) =>
      ["habits", "completions", userId, fromKey, toKey] as const,
    /** History of SINGLE habit (per-habit calendar). */
    habitCompletions: (habitId: string | undefined) =>
      ["habits", "habit-completions", habitId] as const,
    /** User reminders. */
    reminders: (userId: string | undefined) => ["habits", "reminders", userId] as const,
    /** Synchronized definition versions of user (per-habit timeline). */
    versions: (userId: string | undefined) => ["habits", "versions", userId] as const,
    /** Versions (offline window) of SINGLE habit (history in detail). */
    habitVersions: (habitId: string | undefined) => ["habits", "habit-versions", habitId] as const,
    /** Habit count (profile stats). */
    count: (userId: string | undefined) => ["habits", "count", userId] as const,
    /** Trash (soft-deleted habits). */
    trashed: (userId: string | undefined) => ["habits", "trashed", userId] as const,
    /** User streak protectors (inventory + spent). */
    protectors: (userId: string | undefined) => ["habits", "protectors", userId] as const,
  },
  search: {
    all: ["search"] as const,
    results: (query: string, filter: string) => ["search", "results", query, filter] as const,
  },
  powersync: {
    /** Upload queue stats (sync indicator).*/
    uploadStats: (userId: string | undefined) => ["powersync", "uploadStats", userId] as const,
    /** Is there local data? (cleanup button in profile).*/
    hasLocalData: (userId: string | undefined) => ["powersync", "hasLocalData", userId] as const,
  },
  themes: {
    all: ["themes"] as const,
    /** Complete theme catalog (unpaginated getThemesQuery; profile + gallery SHARE cache). */
    catalog: () => ["themes", "catalog"] as const,
    /** Gallery of all available themes (paginated).*/
    gallery: (limit?: number) => ["themes", "gallery", limit] as const,
    /** Active theme row resolved for a specific user.*/
    active: (userId: string | undefined) => ["themes", "active", userId] as const,
    /** Raw user_settings row (stores selected theme id).*/
    userSettings: (userId: string | undefined) => ["themes", "userSettings", userId] as const,
  },
  userPreferences: {
    /** Consolidated user_preferences row (language, font, theme).*/
    consolidated: (userId: string | undefined) =>
      ["userPreferences", "consolidated", userId] as const,
  },
  fonts: {
    all: ["fonts"] as const,
    families: (category?: string) => ["fonts", "families", category] as const,
    family: (id: string | null) => ["fonts", "family", id] as const,
    familyVariants: (id: string | null) => ["fonts", "variants", id] as const,
    variants: () => ["fonts", "variants"] as const,
    /** Active font family for a user (from user_preferences).*/
    userFamily: (userId: string | undefined) => ["fonts", "userFamily", userId] as const,
    /** Full font families catalog (profile font selector).*/
    catalogFamilies: () => ["fonts", "catalogFamilies"] as const,
    /** Full font variants catalog (profile font selector).*/
    catalogVariants: () => ["fonts", "catalogVariants"] as const,
    /** Presence probe: is the synced catalog available locally at all?*/
    catalogPresence: () => ["fonts", "catalogPresence"] as const,
  },
  /** Admin font management (server REST, not PowerSync).*/
  adminFonts: {
    /** The approved catalog (listAll). Prefix of `registry` so invalidating it
     *  cascades to both lists.*/
    all: ["admin", "fonts"] as const,
    /** Third-party registry browser, keyed by the active filters.*/
    registry: (search: string, category: string) =>
      ["admin", "fonts", "registry", search, category] as const,
  },
  auth: {
    invitations: () => ["auth", "listInvitations"] as const,
    organizations: () => ["auth", "listOrganizations"] as const,
    members: (organizationId: string) =>
      ["auth", "getOrganizationMembers", { organizationId }] as const,
    session: () => ["auth", "session"] as const,
    /** Active user sessions (profile → "Active sessions"). Invalidated
     *  after revoking so that the revoked device disappears from the list. */
    activeSessions: () => ["auth", "activeSessions"] as const,
    socialProviders: () => ["auth", "socialProviders"] as const,
    loginMethods: () => ["auth", "loginMethods"] as const,
  },
};
